import type {
  FiscalInvoiceDetails,
  FiscalInvoiceSummary,
  InvoiceDirection,
  InvoiceImportPreview,
  InvoicesSummary,
  InvoiceStatus,
  Paginated,
} from '@ldembalagens/types';
import { api, apiFetch, type QueryParams } from '@/lib/api-client';

export interface InvoiceQuery extends QueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  direction?: InvoiceDirection;
  status?: InvoiceStatus;
  supplierId?: string;
  customerId?: string;
  pendingStock?: boolean;
  from?: string;
  to?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface InvoiceItemInput {
  code?: string | null;
  description: string;
  ncm?: string | null;
  cfop?: string | null;
  unit?: string;
  quantity: string;
  unitPrice: string;
  discount?: string;
  icms?: string;
  ipi?: string;
  pis?: string;
  cofins?: string;
  materialId?: string | null;
  productId?: string | null;
}

export interface InvoiceInput {
  direction: InvoiceDirection;
  number: string;
  series?: string | null;
  model?: string | null;
  accessKey?: string | null;
  issuedAt: string;
  issuerDocument?: string | null;
  issuerName?: string | null;
  recipientDocument?: string | null;
  recipientName?: string | null;
  supplierId?: string | null;
  customerId?: string | null;
  purchaseOrderId?: string | null;
  salesOrderId?: string | null;
  totalFreight?: string;
  status?: InvoiceStatus;
  protocol?: string | null;
  notes?: string | null;
  items: InvoiceItemInput[];
}

/** Envia multipart (arquivo + campos) mantendo autenticação/refresh do cliente HTTP. */
export function multipart(file: File, fields: Record<string, string | undefined> = {}): FormData {
  const form = new FormData();
  form.append('file', file, file.name);
  for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== '') form.append(k, v);
  return form;
}

export const invoicesApi = {
  summary: () => api.get<InvoicesSummary>('/invoices/summary'),
  list: (q: InvoiceQuery) => api.get<Paginated<FiscalInvoiceSummary>>('/invoices', q),
  get: (id: string) => api.get<FiscalInvoiceDetails>(`/invoices/${id}`),
  preview: (file: File) =>
    apiFetch<InvoiceImportPreview>('/invoices/import/preview', {
      method: 'POST',
      body: multipart(file),
    }),
  importXml: (file: File, fields: Record<string, string | undefined>) =>
    apiFetch<FiscalInvoiceDetails>('/invoices/import', {
      method: 'POST',
      body: multipart(file, fields),
    }),
  create: (i: InvoiceInput) => api.post<FiscalInvoiceDetails>('/invoices', i),
  update: (
    id: string,
    i: Partial<
      Pick<
        InvoiceInput,
        | 'number'
        | 'series'
        | 'issuedAt'
        | 'issuerDocument'
        | 'issuerName'
        | 'recipientDocument'
        | 'recipientName'
        | 'supplierId'
        | 'customerId'
        | 'purchaseOrderId'
        | 'salesOrderId'
        | 'status'
        | 'protocol'
        | 'notes'
      >
    >,
  ) => api.patch<FiscalInvoiceDetails>(`/invoices/${id}`, i),
  linkItems: (
    id: string,
    items: { itemId: string; materialId?: string | null; productId?: string | null }[],
  ) => api.post<FiscalInvoiceDetails>(`/invoices/${id}/items/link`, { items }),
  applyStock: (
    id: string,
    i: { warehouseId: string; occurredAt?: string; skipUnlinked?: boolean },
  ) => api.post<FiscalInvoiceDetails>(`/invoices/${id}/stock`, i),
  generateTitles: (
    id: string,
    i: {
      categoryId: string;
      costCenterId?: string | null;
      dueDate: string;
      installments?: number;
      intervalDays?: number;
    },
  ) => api.post<FiscalInvoiceDetails>(`/invoices/${id}/titles`, i),
  attachDanfe: (id: string, file: File) =>
    apiFetch<FiscalInvoiceDetails>(`/invoices/${id}/danfe`, {
      method: 'POST',
      body: multipart(file),
    }),
  cancel: (id: string, reason: string) =>
    api.post<FiscalInvoiceDetails>(`/invoices/${id}/cancel`, { reason }),
  /** Exclusão permanente (cadastro errado): sem estoque aplicado nem títulos ativos. */
  remove: (id: string) => api.delete(`/invoices/${id}`),
};

export const invoicesKeys = {
  all: ['invoices'] as const,
  summary: ['invoices', 'summary'] as const,
  list: (q: object) => ['invoices', 'list', q] as const,
  detail: (id: string) => ['invoices', 'detail', id] as const,
};
